<template>
    <div class="container">
        <h3 class="my-3">{{ $t('nav.calendar') }}</h3>
        <div class="row">
            <div class="col">
                <label class="form-check-label">
                    <input type="checkbox" class="form-check-input" v-model="showFinishedTasks">
                    {{ $t('calendar.showFinished') }}
                </label>
            </div>
            <div class="col">
                <button class="btn btn-primary btn-sm w-100" @click="showAddEventModal(null)">{{ $t('calendar.addEntry') }}</button>
            </div>
        </div>
    </div>
    <FullCalendar ref="fullCalendar" :options='calendarOptions' class="bg-white mt-3" />    
    <AddEventModal :show="isAddEventModalVisible" :event="selectedEvent" @close="isAddEventModalVisible = false" @event-saved="fetchEvents" />
</template>
<script>
import FullCalendar from '@fullcalendar/vue3';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import bootstrap5Plugin from '@fullcalendar/bootstrap5';
import etLocale from '@fullcalendar/core/locales/et';
import axios from '@/services/axios';
import { currentLocale } from '@/i18n';
import { toApiDateTime } from '@/utils/datetime';
import AddEventModal from '@/components/AddEventModal.vue';

const ONE_HOUR_MS = 60 * 60 * 1000;
const ONE_DAY_MS = 24 * ONE_HOUR_MS;

// The grid starts and ends here unless an item falls outside it; slotRangeFor()
// only ever widens the window, never narrows it. Mirrors the same pair in
// forus/page/planner.php, which uses 8..23 for its wider planning board.
const DEFAULT_SLOT_START_HOUR = 8;
const DEFAULT_SLOT_END_HOUR = 18;

function slotTime(hour) {
    return (hour < 10 ? '0' : '') + hour + ':00:00';
}

// Kept out of the token/user keys so the view settings survive a logout.
const VIEW_KEY = 'calendarView';
const SHOW_FINISHED_KEY = 'calendarShowFinished';
const KNOWN_VIEWS = ['timeGridDay', 'timeGridThreeDay', 'timeGridWeek'];

function storedView() {
    const stored = localStorage.getItem(VIEW_KEY);
    // A stale or hand-edited value would make FullCalendar throw on init.
    return KNOWN_VIEWS.includes(stored) ? stored : 'timeGridDay';
}

export default {
    components: {
        FullCalendar,
        AddEventModal
    },
    data() {
        return {
            showFinishedTasks: localStorage.getItem(SHOW_FINISHED_KEY) === 'true',
            isAddEventModalVisible: false,
            selectedEvent: null,
            isSavingDrop: false,
            calendarOptions: {
                locales: [etLocale],
                locale: currentLocale(),
                plugins: [timeGridPlugin, interactionPlugin, bootstrap5Plugin],
                initialView: storedView(),
                height: 'auto',
                nowIndicator: true,
                // Seeded to the default window so the first paint is already
                // 08-18 rather than flashing a full 24-hour grid; the events
                // callback widens it once the day's items are known.
                slotMinTime: slotTime(DEFAULT_SLOT_START_HOUR),
                slotMaxTime: slotTime(DEFAULT_SLOT_END_HOUR),
                // Greys every cell outside core hours via --fc-non-business-color,
                // weekends included. Same values as forus/page/planner.php.
                businessHours: {
                    daysOfWeek: [1, 2, 3, 4, 5],
                    startTime: '09:00',
                    endTime: '17:00',
                },
                // Drag to reschedule, drag the bottom edge to change duration.
                // On touch the resize handles only appear once an event is
                // selected, which the first long-press does.
                editable: true,
                eventStartEditable: true,
                eventDurationEditable: true,
                eventResizableFromStart: false,
                snapDuration: '00:15:00',
                dragRevertDuration: 300,
                // The 1000ms default is slow enough to feel broken on a phone.
                longPressDelay: 400,
                eventLongPressDelay: 400,
                selectLongPressDelay: 400,
                titleFormat: { year: '2-digit', month: '2-digit', day: '2-digit' },
                views: {
                    timeGridThreeDay: {
                        type: 'timeGrid',
                        duration: { days: 3 },
                        buttonText: this.$t('calendar.threeDays')
                    }
                },
                headerToolbar: {
                    left: 'timeGridDay,timeGridThreeDay,timeGridWeek',
                    center: 'prev,title,next',
                    right: ''
                },
                events: (fetchInfo, successCallback, failureCallback) => {
                    axios.get('/events', {
                        params: {
                            start: fetchInfo.startStr,
                            end: fetchInfo.endStr,
                            finished: this.showFinishedTasks ? null : 0
                        }
                    })
                        .then(response => {
                            const events = response.data.data;
                            this.applySlotRange(fetchInfo.start, fetchInfo.end, events);
                            successCallback(events.map(this.withEditableFlag));
                        })
                        .catch(error => {
                            failureCallback(error);
                        });
                },
                // All three views render the same timeGrid component, so
                // switching between them re-renders rather than remounts and
                // viewDidMount never fires again. datesSet does — it also fires
                // on prev/next, hence the compare before writing.
                datesSet: (info) => {
                    if (localStorage.getItem(VIEW_KEY) !== info.view.type) {
                        localStorage.setItem(VIEW_KEY, info.view.type);
                    }
                },
                // FullCalendar calls the top row "all day"; here it means untimed,
                // i.e. no time of day at all. A time entry is a logged amount of
                // hours, so it always has one and cannot go there.
                eventAllow: (dropLocation, draggedEvent) => {
                    if (dropLocation.allDay && draggedEvent.extendedProps.resource_id > 0) {
                        return false;
                    }
                    return true;
                },
                eventDidMount: (info) => {
                    if (info.event.startEditable) {
                        info.el.classList.add('fc-event--draggable');
                    }
                },
                eventDragStart: () => {
                    if (navigator.vibrate) {
                        navigator.vibrate(30);
                    }
                },
                eventDrop: (dropInfo) => {
                    this.saveEventTimes(dropInfo, false);
                },
                eventResize: (resizeInfo) => {
                    this.saveEventTimes(resizeInfo, true);
                },
                eventClick: (clickInfo) => {
                    clickInfo.jsEvent.preventDefault();
                    clickInfo.jsEvent.stopPropagation();
                    if (clickInfo.event.extendedProps.resource_id > 0) {
                        const selectedEvent = {
                            resource_id: clickInfo.event.extendedProps.resource_id,
                            profile_id: clickInfo.event.extendedProps.profile_id,
                            start_time: clickInfo.event.startStr,
                            duration: clickInfo.event.extendedProps.amount,
                            description: clickInfo.event.extendedProps.notes
                        };
                        this.showAddEventModal(selectedEvent);
                    } else if(clickInfo.event.extendedProps.isCalendar == 0) {
                        const taskId = clickInfo.event.id;
                        this.$router.push(`/tasks/${taskId}`);
                    }
                },
            }
        }
    },
    methods: {
        showAddEventModal(event = null) {
            this.selectedEvent = event;
            this.isAddEventModalVisible = true;
        },
        fetchEvents() {
            this.$refs.fullCalendar.getApi().refetchEvents();
        },
        /**
         * The feed's `editable` decides whether the record may be changed at all:
         * for activities it is `!finished && !is_calendar && !confirmed`, so it
         * already rules out finished, confirmed, shifts and reservations. Only
         * one thing is left for the client to add.
         */
        withEditableFlag(event) {
            return { ...event, editable: this.isEditable(event) };
        },
        isEditable(event) {
            if (!event.start) return false;
            if (!event.editable) return false;

            // Untimed time entries (timed_resource = 0, so a date-only start) are
            // left alone: a logged amount of hours belongs in the grid, and
            // PUT /events/resources/{id} cannot clear the flag anyway.
            if (event.resource_id > 0) return !this.isDateOnly(event.start);

            return true;
        },
        isDateOnly(value) {
            return typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value);
        },
        /**
         * An untimed item lives in the all-day row and has no time of day, so it
         * must never drag the grid open. The feed marks them either way round:
         * a date-only start string, or FullCalendar's own allDay flag.
         */
        isUntimed(event) {
            return event.allDay === true || this.isDateOnly(event.start);
        },
        /**
         * Smallest window that still shows every timed item of one day, never
         * narrower than DEFAULT_SLOT_START_HOUR..DEFAULT_SLOT_END_HOUR. Ported
         * from slotRangeForDay() in forus/page/planner.php.
         *
         * dayStart / dayEnd are local midnight to the next local midnight.
         */
        slotRangeForDay(dayStart, dayEnd, events) {
            let minHour = DEFAULT_SLOT_START_HOUR;
            let maxHour = DEFAULT_SLOT_END_HOUR;

            events.forEach(event => {
                if (!event.start || this.isUntimed(event)) {
                    return;
                }
                const start = new Date(event.start);
                const end = event.end ? new Date(event.end) : start;
                if (isNaN(start) || isNaN(end) || end < dayStart || start >= dayEnd) {
                    return;
                }
                // Clamp to the day first: a job running past midnight occupies the
                // top of the NEXT day's column, so that column is the one that
                // needs 00:00 -- not this one.
                const from = start < dayStart ? dayStart : start;
                const to = end > dayEnd ? dayEnd : end;

                minHour = Math.min(minHour, from.getHours());
                // getHours() on next-day midnight is 0, which would silently
                // narrow the window -- reaching or crossing midnight means 24.
                const endHour = to.getTime() >= dayEnd.getTime()
                    ? 24
                    : to.getHours() + (to.getMinutes() || to.getSeconds() ? 1 : 0);
                maxHour = Math.max(maxHour, endHour);
            });

            return { min: minHour, max: maxHour };
        },
        /**
         * Union of slotRangeForDay over every day the view covers. planner.php
         * needs no equivalent -- it runs one single-day calendar per column --
         * but here Week and 3 days are one calendar spanning several days, and
         * each of them can push a different edge of the window.
         */
        slotRangeFor(rangeStart, rangeEnd, events) {
            let minHour = DEFAULT_SLOT_START_HOUR;
            let maxHour = DEFAULT_SLOT_END_HOUR;

            const dayStart = new Date(rangeStart.getFullYear(), rangeStart.getMonth(), rangeStart.getDate());
            while (dayStart < rangeEnd) {
                const dayEnd = new Date(dayStart);
                dayEnd.setDate(dayEnd.getDate() + 1);

                const range = this.slotRangeForDay(dayStart, dayEnd, events);
                minHour = Math.min(minHour, range.min);
                maxHour = Math.max(maxHour, range.max);

                dayStart.setDate(dayStart.getDate() + 1);
            }

            return { min: slotTime(minHour), max: slotTime(maxHour) };
        },
        /**
         * Widen the grid when an item falls outside the default hours.
         *
         * Only writes on an actual change: the options object IS the applied
         * state, and assigning to it re-renders the calendar, which re-runs the
         * events fetch that called this -- an endless refetch loop without the
         * guard. planner.php guards the same way via its appliedSlotRanges array.
         */
        applySlotRange(rangeStart, rangeEnd, events) {
            const range = this.slotRangeFor(rangeStart, rangeEnd, events);
            if (this.calendarOptions.slotMinTime !== range.min) {
                this.calendarOptions.slotMinTime = range.min;
            }
            if (this.calendarOptions.slotMaxTime !== range.max) {
                this.calendarOptions.slotMaxTime = range.max;
            }
        },
        /**
         * Build the task date payload the same way the "planned" date picker in
         * TaskComponent does: an untimed task has no start_date and keeps its
         * date in estimated_date at midnight, a timed one stores both ends.
         *
         * `event.allDay` is FullCalendar's flag for the untimed row.
         */
        taskDatePayload(event, wasUntimed) {
            if (event.allDay) {
                return {
                    start_date: null,
                    estimated_date: toApiDateTime(this.untimedEndDate(event)),
                    timed_due_date: false
                };
            }
            return {
                start_date: toApiDateTime(event.start),
                estimated_date: toApiDateTime(this.timedEndDate(event, wasUntimed)),
                timed_due_date: true
            };
        },
        /**
         * An untimed task spanning a single day carries no duration, so giving it
         * a time makes it an hour. One spanning several days keeps its span.
         */
        timedEndDate(event, wasUntimed) {
            let durationMs = event.end ? event.end - event.start : 0;
            if (wasUntimed && durationMs <= ONE_DAY_MS) {
                durationMs = 0;
            }
            return new Date(event.start.getTime() + (durationMs || ONE_HOUR_MS));
        },
        /** FullCalendar's untimed end is exclusive; the API stores the last day. */
        untimedEndDate(event) {
            if (!event.end) return event.start;
            const end = new Date(event.end);
            end.setDate(end.getDate() - 1);
            return end < event.start ? event.start : end;
        },
        /** Event length in decimal hours, the unit activity_resources.amount uses. */
        durationInHours(event) {
            if (!event.end) return event.extendedProps.amount;
            return Math.round((event.end - event.start) / ONE_HOUR_MS * 10000) / 10000;
        },
        /**
         * Persists a drag or a resize. `durationChanged` tells the two apart:
         * on a plain move the stored amount is reused as-is rather than
         * recomputed, so its original precision survives the round trip.
         */
        async saveEventTimes(info, durationChanged) {
            if (this.isSavingDrop) {
                info.revert();
                return;
            }

            const event = info.event;

            this.isSavingDrop = true;

            try {
                let response;
                if (event.extendedProps.resource_id > 0) {
                    // Full replace with no partial update — resend every field or
                    // the untouched ones get wiped.
                    response = await axios.put(`/events/resources/${event.extendedProps.resource_id}`, {
                        profile_id: event.extendedProps.profile_id,
                        start_time: toApiDateTime(event.start),
                        duration: durationChanged ? this.durationInHours(event) : event.extendedProps.amount,
                        description: event.extendedProps.notes
                    });
                } else {
                    response = await axios.patch(`/tasks/${event.id}`, this.taskDatePayload(event, info.oldEvent.allDay));
                }

                if (response.data && response.data.success !== true) {
                    throw new Error(response.data.error?.message);
                }

                this.$root.$refs.globalAlert?.showAlert(this.$t('calendar.moved'), 'success');
                this.fetchEvents();
            } catch (error) {
                console.error('Error moving event:', error);
                info.revert();
                this.$root.$refs.globalAlert?.showAlert(this.$t('calendar.moveFailed'), 'danger');
            } finally {
                this.isSavingDrop = false;
            }
        }
    },
    watch: {
        showFinishedTasks(value) {
            localStorage.setItem(SHOW_FINISHED_KEY, JSON.stringify(value));
            this.fetchEvents();
        }
    }
}
</script>
<style>
.fc-toolbar {
    display: block !important;
    margin: 0 5px 0 5px;
}

.fc-timegrid-axis-cushion {
    font-size: 10px !important;
}

.fc-col-header-cell-cushion {
    font-size: 14px !important;
    text-decoration: none !important;
}

.fc-timegrid-slot-label-cushion {
    font-size: 14px !important;
}

.fc-event-main {
    font-size: 12px !important;
}

/* Long-pressing to drag otherwise fires the iOS text-selection callout. */
.fc-event {
    -webkit-touch-callout: none !important;
    -webkit-user-select: none !important;
    user-select: none !important;
}

.fc-event--draggable {
    cursor: grab;
    border-left-width: 4px !important;
    border-left-style: solid !important;
}

.fc-event--draggable:active,
.fc-event-dragging {
    cursor: grabbing;
}

/* Bigger resize dot — the 8px default is a hard target with a fingertip. */
.fc {
    --fc-event-resizer-dot-total-width: 14px;
}

.fc-toolbar-chunk {
    width: 100%;
}

.fc-button-group {
    width: 100%;
}

.fc-timeGridDay-button,
.fc-timeGridThreeDay-button,
.fc-timeGridWeek-button {
    background-color: var(--bs-light) !important;
    font-size: 14px !important;
    color: var(--bs-primary) !important;
    border: 0 !important;
    border-radius: 40px !important;
    outline: none !important;
    box-shadow: none !important;
}

.fc-timeGridDay-button:focus,
.fc-timeGridThreeDay-button:focus,
.fc-timeGridWeek-button:focus {
    outline: none !important;
    box-shadow: none !important;
}

.fc-timeGridDay-button.fc-button-active,
.fc-timeGridThreeDay-button.fc-button-active,
.fc-timeGridWeek-button.fc-button-active {
    background-color: var(--bs-red) !important;
    color: var(--bs-white) !important;
}

.fc-prev-button,
.fc-next-button {
    width: 40px;
    height: 40px;
    font-size: 14px !important;
    background-color: var(--bs-white) !important;
    color: var(--bs-primary) !important;
    border: 1px solid var(--bs-red) !important;
    border-radius: 12px !important;
    outline: none !important;
    box-shadow: none !important;
}

.fc-toolbar-chunk .fc-button-group {
    border-radius: 40px;
    padding: 6px;
    margin-top: 1rem;
    margin-bottom: 1rem;
    background-color: var(--bs-light) !important;
    border: 1px solid var(--bs-border-color);
}

.fc-toolbar-title {
    display: inline;
}

.fc-header-toolbar:nth-child(1) .fc-toolbar-chunk div {
    display: flex;
    justify-content: space-between;
}

.fc-toolbar-title {
    font-size: 22px !important;
}
</style>